import React from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRIES, COUNTRY_NAME_TO_CODE } from '../constants';
import '../styles/ContactForm.css';

interface SelectFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const SelectField: React.FC<SelectFieldProps> = ({
  id,
  name,
  label,
  value,
  error,
  required = false,
  placeholder,
  options,
  onChange
}) => {
  const { t } = useTranslation();
  const requiredLabel = t('accessibility.required');

  // If no options provided and this is a country field, use countries
  const fieldOptions = options || (name === 'country' ? COUNTRIES.map((englishName) => {
    const code = COUNTRY_NAME_TO_CODE[englishName];
    const translatedName = code ? t(`countries.${code}`) : englishName;
    return {
      value: englishName,
      label: translatedName
    };
  }).sort((a, b) => a.label.localeCompare(b.label)) : []);

  return (
    <div className="form-group">
      <label htmlFor={id}>
        {label} {required && <span aria-label={requiredLabel}>*</span>}
      </label>
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={!!error}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {fieldOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <div id={`${id}-error`} className="form-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default SelectField;

